import {
  MS_BANKING_DETAILS,
  MS_COMPENSATION,
  MS_EMPLOYMENT_INFORMATION,
  MS_PROFILE_SETTINGS_INFO,
  MS_UPDATE_PASSWORD,
  MS_GET_USER_PROFILE,
  MS_USER_ROLES_ME,
  MS_UPLOAD_PROFILE_IMAGE,
  MS_USER_CUSTOM_STATUS,
  CREATE_COMPANY,
  CREATE_COMPANY_INDUSTRY_TYPES,
  SWITCH_COMPANY,
} from "@/constants/apiEndpoints";
import type {
  IBankDetailsData,
  TBankDetailsResponse,
  IEmployeeData,
  IPasswordUpdateRequest,
  IPersonalDetails,
  TPersonalDetailsResponse,
  TEmploymentDetailsResponse,
  ICreateCompanyPayload,
  ISingleCompanyData,
} from "@/types/(my-space)/users.interface";

import { BUILTIN_MODULE_ICONS } from "@/constants/module-icons";
import { createApi } from "@reduxjs/toolkit/query/react";
import { setIsAdmin, setUserSlice } from "@/store/slices/user.slice";
import { dynamicBaseQuery } from "@/api/DynamicBaseQuery";
import type { IApiResponse } from "@/types";
import type { IGetSidebarLinksResponse } from "@/types/(company)/roles.interface";

export const userApi = createApi({
  reducerPath: "userApi",
  baseQuery: dynamicBaseQuery,
  tagTypes: [
    "getUser",
    "getBankDetails",
    "getEmploymentsInfo",
    "getProfileSettingsInfo",
    "getSuperAdminCompanies",
    "getEmployeeCompanies",
    "getSidebarPersmissions",
  ],
  endpoints: (builder) => ({
    getUser: builder.query({
      query: () => ({
        url: MS_GET_USER_PROFILE,
        method: "GET",
      }),
      providesTags: ["getUser"],
      onQueryStarted: async (_body, { dispatch, queryFulfilled }) => {
        try {
          const { data } = await queryFulfilled;
          if (data.data) dispatch(setUserSlice({ user: data.data }));
        } catch (error: any) {
          console.error(error.data.message || "Profile fetch failed");
        }
      },
    }),
    getSidebarPersmissions: builder.query<
      IApiResponse<IGetSidebarLinksResponse>,
      void
    >({
      query: () => ({
        url: MS_USER_ROLES_ME,
      }),
      // Modules with a built-in icon (Chat) get it until one is uploaded.
      transformResponse: (res: IApiResponse<IGetSidebarLinksResponse>) => {
        const withIcon = <T extends { key: string; icon: string | null }>(l: T) =>
          l.icon ? l : { ...l, icon: BUILTIN_MODULE_ICONS[l.key] ?? null };
        if (!res?.data) return res;
        return {
          ...res,
          data: {
            ...res.data,
            individual: (res.data.individual ?? []).map(withIcon),
            company: (res.data.company ?? []).map(withIcon),
          },
        };
      },
      providesTags: ["getSidebarPersmissions"],
      onQueryStarted: async (_body, { dispatch, queryFulfilled }) => {
        try {
          const { data: sidebarLinks } = await queryFulfilled;
          dispatch(setIsAdmin(sidebarLinks?.data?.company?.length > 0));
        } catch (error: any) {
          console.error(
            error.data.message || "Sidebar Permission fetch failed",
          );
        }
      },
    }),
    getProfileSettingsInfo: builder.query<TPersonalDetailsResponse, void>({
      query: () => ({
        url: MS_PROFILE_SETTINGS_INFO,
        method: "GET",
      }),
      providesTags: ["getProfileSettingsInfo"],
    }),
    updateProfileSettingsInfo: builder.mutation<
      TPersonalDetailsResponse,
      Partial<IPersonalDetails>
    >({
      query: (body) => ({
        url: MS_PROFILE_SETTINGS_INFO,
        method: "PUT",
        body,
      }),
      invalidatesTags: ["getProfileSettingsInfo"],
    }),
    uploadProfileImage: builder.mutation<
      any,
      { file: File; employeeId?: number }
    >({
      query: ({ file, employeeId }) => {
        const formData = new FormData();
        formData.append("file", file);

        return {
          url: MS_UPLOAD_PROFILE_IMAGE,
          method: "POST",
          body: formData,
          params: {
            employeeId,
          },
        };
      },
      invalidatesTags: ["getProfileSettingsInfo"],
    }),
    getCompensationInfo: builder.query({
      query: () => ({
        url: MS_COMPENSATION,
        method: "GET",
      }),
    }),
    getEmploymentsInfo: builder.query<TEmploymentDetailsResponse, void>({
      query: () => ({
        url: MS_EMPLOYMENT_INFORMATION,
        method: "GET",
      }),
      providesTags: ["getEmploymentsInfo"],
    }),
    updateEmploymentInfo: builder.mutation<
      TEmploymentDetailsResponse,
      Partial<IEmployeeData>
    >({
      query: (body) => ({
        url: MS_EMPLOYMENT_INFORMATION,
        method: "PUT",
        body,
      }),
      invalidatesTags: ["getEmploymentsInfo"],
    }),
    updatePassword: builder.mutation<
      TPersonalDetailsResponse,
      IPasswordUpdateRequest
    >({
      query: (body) => ({
        url: MS_UPDATE_PASSWORD,
        method: "PUT",
        body,
      }),
    }),
    getBankingDetails: builder.query({
      query: () => ({
        url: MS_BANKING_DETAILS,
        method: "GET",
      }),
      providesTags: ["getBankDetails"],
    }),
    updateBankDetails: builder.mutation<
      TBankDetailsResponse,
      Partial<IBankDetailsData>
    >({
      query: (body) => ({
        url: MS_BANKING_DETAILS,
        method: "PUT",
        body,
      }),
      invalidatesTags: ["getBankDetails"],
    }),
    createCompany: builder.mutation<
      IApiResponse<ISingleCompanyData>,
      ICreateCompanyPayload
    >({
      query: (body) => ({
        url: CREATE_COMPANY,
        method: "POST",
        body,
      }),
    }),
    getCreateCompanyIndustryTypes: builder.query<IApiResponse<void>, void>({
      query: () => ({
        url: CREATE_COMPANY_INDUSTRY_TYPES,
        method: "GET",
      }),
    }),
    switchCompany: builder.mutation<
      IApiResponse<ISingleCompanyData>,
      { companyId: number }
    >({
      query: ({ companyId }) => ({
        url: SWITCH_COMPANY(companyId),
        method: "PUT",
      }),
      invalidatesTags: ["getSuperAdminCompanies", "getEmployeeCompanies"],
    }),
    updateUserCustomStatus: builder.mutation<IApiResponse<{ message: string }>, { status: string }>({
      query: (body) => ({
        url: MS_USER_CUSTOM_STATUS,
        method: "PUT",
        body,
      }),
      invalidatesTags: ["getUser"],
    }),
  }),
});

export const {
  useGetUserQuery,
  useLazyGetUserQuery,
  useGetSidebarPersmissionsQuery,
  useGetProfileSettingsInfoQuery,
  useUpdateProfileSettingsInfoMutation,
  useUploadProfileImageMutation,
  useGetBankingDetailsQuery,
  useGetCompensationInfoQuery,
  useGetEmploymentsInfoQuery,
  useUpdateEmploymentInfoMutation,
  useUpdatePasswordMutation,
  useUpdateBankDetailsMutation,
  useCreateCompanyMutation,
  useGetCreateCompanyIndustryTypesQuery,
  useSwitchCompanyMutation,
  useUpdateUserCustomStatusMutation,
} = userApi;
